import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { BehaviorSubject, Subject } from 'rxjs';
import { BusinessUnitModel } from '@fi-app/business-unit/business-unit.model';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { DataFieldModel } from '@fi-app/data-field/data-field.model';
import { FiImageSize, ModuleType, ObjectTypes } from '@fi-shared/types';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { DomSanitizer } from '@angular/platform-browser';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { takeUntil } from 'rxjs/operators';
import { IBusinessUnitViewDialogData } from '@fi-app/business-unit/business-unit';

@Component({
    selector: 'fi-business-unit-view-dialog',
    templateUrl: './business-unit-view-dialog.component.html',
    styleUrls: ['./business-unit-view-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-business-unit-view-dialog' },
    standalone: false,
})
export class BusinessUnitViewDialogComponent
    extends FiDialogsContentBase<IBusinessUnitViewDialogData, { businessUnit: BusinessUnitModel }>
    implements OnInit
{
    private fiMediaStorageService: FiMediaStorageService;
    private sanitizer: DomSanitizer;
    private statisticService: StatisticService;
    private fiStateService: FiStateService;

    loading$ = new BehaviorSubject<boolean>(false);
    protected get accessRightFilterCollection(): IAccessRightFilterCollection {
        return this.data.accessRightFilterCollection;
    }
    protected get businessUnit(): BusinessUnitModel {
        return this.data.businessUnit;
    }
    protected get businessUnitDataFields(): DataFieldModel[] {
        return this.data.businessUnitDataFields;
    }
    protected get hasEditAccess(): boolean {
        return this.data.hasEditAccess;
    }
    protected get hasDeleteAccess(): boolean {
        return this.data.hasDeleteAccess;
    }
    FiStateNames = FiStateNames;

    ngOnInit(): void {
        this.setServices();
        this.subscribeOnStateChange();
        this.statisticService.Visit(ModuleType.Core, ObjectTypes.BusinessUnit, this.businessUnit.Id);
    }

    private subscribeOnStateChange() {
        this.fiStateService.onFiStateGo$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.closeDialog());
    }

    editBusinessUnit(stateName: string) {
        this.fiStateService.go(stateName, { Id: this.businessUnit.Id });
        this.closeDialog();
    }

    deleteBusinessUnit() {
        this.closeDialog(this.businessUnit);
    }

    closeDialog(businessUnit?: BusinessUnitModel) {
        this.dialogRef.close({ value: { businessUnit } });
    }

    getBusinessUnitImage() {
        if (this.businessUnit.MediaBankPhysicalFileName) {
            const url = this.fiMediaStorageService.getMediaFileUrl(
                {
                    PhysicalFileName: this.businessUnit.MediaBankPhysicalFileName,
                    Extension: this.businessUnit.MediaBankExtension,
                },
                { size: FiImageSize.Medium },
            );
            return this.sanitizer.bypassSecurityTrustStyle(`url(${url})`);
        }
    }

    private setServices() {
        this.fiMediaStorageService = this.injector.get(FiMediaStorageService);
        this.sanitizer = this.injector.get(DomSanitizer);
        this.statisticService = this.injector.get(StatisticService);
        this.fiStateService = this.injector.get(FiStateService);
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
